import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import { isDarkMode } from "../../utils/constants";

const accentColor = isDarkMode ? "#9BDDD5" : "#08796F";
const accentText = isDarkMode ? "#071B19" : "#FFFFFF";
const mutedText = isDarkMode ? "rgba(247,247,247,0.72)" : "rgba(21,21,21,0.66)";
const bodyText = isDarkMode ? "rgba(247,247,247,0.84)" : "rgba(21,21,21,0.78)";
const borderColor = isDarkMode ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.14)";
const panelColor = isDarkMode ? "rgba(255,255,255,0.045)" : "rgba(8,121,111,0.045)";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "calc(100vw - 28px)", sm: 488 },
  maxWidth: 488,
  maxHeight: "calc(100vh - 40px)",
  overflowY: "auto",
  bgcolor: isDarkMode ? "#181818" : "#FFFFFF",
  color: isDarkMode ? "#F7F7F7" : "#151515",
  borderRadius: "8px",
  border: isDarkMode ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
  boxShadow: isDarkMode
    ? "0 22px 70px rgba(0,0,0,0.62)"
    : "0 22px 70px rgba(19,30,42,0.18)",
  outline: "none",
};

const ServiceTipPromptModal = ({
  open,
  prompt,
  onClose,
  onDismiss,
  onShown,
  onCheckout,
  onPaymentMethodStatus,
  onSavedPaymentTip,
}) => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({ available: false });
  const [fallbackCheckoutUrl, setFallbackCheckoutUrl] = useState(null);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState(null);

  const options = useMemo(() => {
    const amounts = prompt?.amountOptionsMinorUnits || [];
    const labels = prompt?.amountOptionLabels || [];
    return amounts.map((amount, index) => ({
      amount,
      label: labels[index] || `${prompt?.currency?.toUpperCase() || ""} ${amount}`,
      recommended: amount === prompt?.recommendedAmountMinorUnits,
    }));
  }, [prompt]);

  const selectedOption = useMemo(
    () => options.find((option) => option.amount === selectedAmount) || options[0],
    [options, selectedAmount],
  );

  useEffect(() => {
    if (open && prompt?.id && typeof onShown === "function") {
      onShown(prompt.id);
    }
  }, [open, prompt?.id, onShown]);

  useEffect(() => {
    if (!open || !prompt?.id) return;

    const defaultOption =
      options.find((option) => option.recommended) ||
      options[Math.min(1, options.length - 1)] ||
      options[0];
    setSelectedAmount(defaultOption?.amount || null);
    setError(null);
    setFallbackCheckoutUrl(null);
    setPaid(false);
  }, [open, prompt?.id, options]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !prompt?.id) return undefined;

    if (typeof onPaymentMethodStatus !== "function") {
      setPaymentMethod({ available: false });
      return undefined;
    }

    setPaymentMethodLoading(true);
    setPaymentMethod({ available: false });
    onPaymentMethodStatus(prompt.id)
      .then((result) => {
        if (!cancelled) {
          setPaymentMethod(result || { available: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPaymentMethod({ available: false, error: "Could not check saved card." });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentMethodLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, prompt?.id, onPaymentMethodStatus]);

  const handleClose = async () => {
    if (!paid && prompt?.id && typeof onDismiss === "function") {
      await onDismiss(prompt.id);
    }
    onClose();
  };

  const redirectToCheckout = (checkoutUrl) => {
    window.location.href = checkoutUrl;
  };

  const handleCheckout = async (amount = selectedAmount) => {
    if (!amount || typeof onCheckout !== "function") return;

    setError(null);
    setCheckoutLoading(true);
    try {
      const result = await onCheckout(prompt.id, amount);
      if (result?.checkoutUrl) {
        redirectToCheckout(result.checkoutUrl);
        return;
      }
      setError(result?.error || "Could not start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAmount || !prompt?.id) return;

    setError(null);
    setFallbackCheckoutUrl(null);

    if (!paymentMethod?.available || typeof onSavedPaymentTip !== "function") {
      await handleCheckout(selectedAmount);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSavedPaymentTip(prompt.id, selectedAmount);
      if (result?.status === "paid") {
        setPaid(true);
        return;
      }

      if (result?.checkoutUrl) {
        setFallbackCheckoutUrl(result.checkoutUrl);
      }
      setError(result?.error || "The saved card could not be charged.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseDifferentCard = () => {
    if (fallbackCheckoutUrl) {
      redirectToCheckout(fallbackCheckoutUrl);
      return;
    }
    handleCheckout(selectedAmount);
  };

  if (!prompt) return null;

  const busy = submitting || checkoutLoading;
  const savedCardAvailable = Boolean(paymentMethod?.available);
  const methodCopy = paymentMethodLoading
    ? "Checking saved card..."
    : savedCardAvailable
      ? `Using ${paymentMethod.label || "saved card"}`
      : "Secure checkout will save this card for future service tips.";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="service-tip-title"
      aria-describedby="service-tip-body"
    >
      <Box sx={modalStyle}>
        <Box sx={{ height: 3, bgcolor: accentColor }} />
        <IconButton
          aria-label="Close tip prompt"
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: isDarkMode ? "rgba(247,247,247,0.82)" : "rgba(21,21,21,0.72)",
            bgcolor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            "&:hover": {
              bgcolor: isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: { xs: 2.25, sm: 2.75 }, pt: { xs: 2.4, sm: 2.8 } }}>
          {paid ? (
            <Box sx={{ textAlign: "center", px: { xs: 0, sm: 1 }, py: 1.5 }}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDarkMode ? "rgba(155,221,213,0.12)" : "rgba(8,121,111,0.10)",
                  color: accentColor,
                  mb: 1.5,
                }}
              >
                <CheckCircleRoundedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography
                id="service-tip-title"
                variant="h6"
                sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.12, letterSpacing: 0, mb: 1 }}
              >
                Service tip submitted
              </Typography>
              <Typography id="service-tip-body" sx={{ color: bodyText, fontSize: 14.5, lineHeight: 1.55, mb: 2.4 }}>
                Thanks for recognizing the quality of the Incite decision support on this outcome.
              </Typography>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  minHeight: 46,
                  borderRadius: "8px",
                  bgcolor: accentColor,
                  color: accentText,
                  fontWeight: 800,
                  textTransform: "none",
                  px: 3,
                  "&:hover": { bgcolor: isDarkMode ? "#A6E7DF" : "#096E65" },
                }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 4, mb: 1.35 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    bgcolor: isDarkMode ? "rgba(155,221,213,0.12)" : "rgba(8,121,111,0.10)",
                    color: accentColor,
                  }}
                >
                  <PaidRoundedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: mutedText,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0,
                      lineHeight: 1.2,
                      textTransform: "uppercase",
                      mb: 0.45,
                    }}
                  >
                    {prompt.serviceLabel}
                  </Typography>
                  <Typography
                    id="service-tip-title"
                    variant="h6"
                    sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 800, lineHeight: 1.12, letterSpacing: 0 }}
                  >
                    {prompt.headline}
                  </Typography>
                </Box>
              </Box>

              <Typography id="service-tip-body" sx={{ fontSize: 14.5, lineHeight: 1.52, color: bodyText, mb: 2 }}>
                {prompt.body}
              </Typography>

              <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: mutedText, fontSize: 12, fontWeight: 800, lineHeight: 1.2 }}>
                  Fixed service tip
                </Typography>
                <Typography sx={{ color: mutedText, fontSize: 11.5, lineHeight: 1.2 }}>
                  No subscription change
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
                {options.map((option) => {
                  const selected = selectedAmount === option.amount;
                  return (
                    <Button
                      key={option.amount}
                      variant="outlined"
                      aria-pressed={selected}
                      disabled={busy}
                      onClick={() => setSelectedAmount(option.amount)}
                      sx={{
                        minHeight: { xs: 54, sm: 62 },
                        borderRadius: "8px",
                        borderWidth: 1,
                        borderColor: selected ? accentColor : borderColor,
                        bgcolor: selected ? (isDarkMode ? "rgba(155,221,213,0.12)" : "rgba(8,121,111,0.08)") : "transparent",
                        color: isDarkMode ? "#F7F7F7" : "#151515",
                        textTransform: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                        "&:hover": {
                          borderColor: accentColor,
                          bgcolor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(8,121,111,0.06)",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 850, lineHeight: 1.12 }}>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 800, lineHeight: 1.1, color: mutedText }}>
                        {option.recommended ? "Suggested" : "Tip amount"}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>

              <Box
                sx={{
                  mt: 1.35,
                  p: 1.15,
                  borderRadius: "8px",
                  border: `1px solid ${borderColor}`,
                  bgcolor: panelColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.25,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <CreditCardRoundedIcon sx={{ fontSize: 18, color: accentColor, flex: "0 0 auto" }} />
                  <Typography sx={{ color: bodyText, fontSize: 12.5, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                    {methodCopy}
                  </Typography>
                </Box>
                {paymentMethodLoading && <CircularProgress size={17} sx={{ color: accentColor, flex: "0 0 auto" }} />}
              </Box>

              {error && (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.15,
                    borderRadius: "8px",
                    bgcolor: isDarkMode ? "rgba(214,69,69,0.10)" : "rgba(214,69,69,0.08)",
                    border: "1px solid rgba(214,69,69,0.35)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.9,
                  }}
                >
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#D64545", mt: 0.05 }} />
                  <Typography sx={{ color: isDarkMode ? "#FFB4B4" : "#9F2525", fontSize: 12.5, lineHeight: 1.4 }}>
                    {error}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mt: 1.45 }}>
                <Button
                  variant="contained"
                  disabled={busy || paymentMethodLoading || !selectedOption}
                  onClick={handleSubmit}
                  sx={{
                    minHeight: 48,
                    borderRadius: "8px",
                    bgcolor: accentColor,
                    color: accentText,
                    fontWeight: 850,
                    textTransform: "none",
                    flex: 1,
                    "&:hover": { bgcolor: isDarkMode ? "#A6E7DF" : "#096E65" },
                  }}
                >
                  {busy
                    ? (submitting ? "Submitting..." : "Opening checkout...")
                    : savedCardAvailable
                      ? `Submit ${selectedOption?.label || ""} tip`
                      : `Continue with ${selectedOption?.label || ""}`}
                </Button>
                {(savedCardAvailable || fallbackCheckoutUrl) && (
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={handleUseDifferentCard}
                    sx={{
                      minHeight: 48,
                      borderRadius: "8px",
                      borderColor,
                      color: isDarkMode ? "#F7F7F7" : "#151515",
                      fontWeight: 800,
                      textTransform: "none",
                      px: 2,
                      "&:hover": {
                        borderColor: accentColor,
                        bgcolor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(8,121,111,0.06)",
                      },
                    }}
                  >
                    Use another card
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ServiceTipPromptModal;
